import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  CompassColor,
  Link,
  Loader,
  Text,
} from "@noom/wax-component-library";

import { QuestionDefinition } from "@/components/survey/Question";
import {
  BaseOutline,
  InformationalScreen,
} from "@/components/survey/questionTypes/core";
import { useAppContext } from "@/contexts";
import { useEnrollmentEvent, useTrackEvent } from "@/hooks";
import { getDisplayName } from "@/models";
import { isCareFirstPartner, isHighmarkPartner } from "@/utils/partners";

// This questions is only reachable via direct navigation. Use caution when updating this ID.
const QUESTION_ID = "ineligible";

export const ScreenedOut: React.FC = () => {
  const {
    eligibilityInfo: { eligiblePrograms = [] } = {},
    partnerInfo,
    screenedOutReason,
  } = useAppContext();
  const { trackAnonEvent, trackIdentifiedEvent, trackSensitiveEvent } =
    useTrackEvent();
  const { sendScreenedOut } = useEnrollmentEvent();
  useTranslation();

  const isCareFirst = isCareFirstPartner(partnerInfo);
  const partnerName = getDisplayName(partnerInfo);
  const showHighmarkPromoCode =
    isHighmarkPartner(partnerInfo) && eligiblePrograms.includes("WEIGHT");

  const isHighmark = isHighmarkPartner(partnerInfo);

  const highmarkMemberPortalSnippet = isHighmark ? (
    <Trans i18nKey="screenedOut.highmarkMemberPortalSnippet" />
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );

  const highmarkPromoCodeSnippet = showHighmarkPromoCode ? (
    <Trans i18nKey="screenedOut.highmarkPromoCodeSnippet" />
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );

  const noomLink = (
    <Link
      color={CompassColor.white}
      href="https://www.noom.com"
      textDecoration="underline"
    />
  );

  useEffect(() => {
    if (!screenedOutReason) return;
    trackAnonEvent("b2bEnrollmentAnonUserExcluded", {
      exclusionReason: screenedOutReason,
    });

    trackIdentifiedEvent("b2bEnrollmentIdentifiedUserExcluded", {});

    trackSensitiveEvent("b2bEnrollmentSensitiveUserExcluded", {
      exclusionReason: screenedOutReason,
    });

    sendScreenedOut(screenedOutReason);
  }, [screenedOutReason]);

  if (!screenedOutReason) {
    return (
      <BaseOutline>
        <Box m="auto">
          <Loader colorScheme="primary" />
        </Box>
      </BaseOutline>
    );
  }

  return (
    <InformationalScreen
      heading={
        isCareFirst ? undefined : <Trans i18nKey="screenedOut.heading" />
      }
      questionId={QUESTION_ID}
    >
      <Text color={CompassColor.white} fontSize="18px" lineHeight="26px">
        <Trans
          components={{
            highmarkMemberPortalSnippet,
            highmarkPromoCodeSnippet,
            noomLink,
          }}
          context={isCareFirst ? "carefirst" : ""}
          i18nKey="screenedOut.body"
          values={{ partnerName }}
        />
      </Text>
    </InformationalScreen>
  );
};

export const Q_SCREENED_OUT: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: () => false,
  component: ScreenedOut,
};
